import { BadgeOutlined as BadgeOutlinedIcon } from 'components/icons';
import PermissionButton from 'components/buttons/PermissionButton';
import ListCreateButton from 'components/buttons/ListCreateButton';
import PartyName from 'components/party/PartyName';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import CopyToClipboardIcon from 'components/util/CopyToClipboard';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLazyGetContactsWithFiltersQuery } from 'store/api/contacts';
import { ContactDto, ContactListItem, PartyType, RelationshipTypeCode } from 'types/contacts';
import { t } from 'utils/getTranslations';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';

interface Props {
    contactId?: string;
    contact?: ContactDto;
}

const getContactHref = (contact: ContactListItem) => `/contact/${contact.id}`;

const EmployeesView: React.FC<Props> = ({ contactId, contact }) => {
    const navigateWithReturn = useNavigateWithReturnState();

    const employeeIds = useMemo(() => {
        if (!contact?.relations) return [];
        return contact.relations.flatMap((r) => (r.partyRelationshipTypeCode === 'EMPLOYEE' ? [r.otherParty.id] : []));
    }, [contact]);

    const passiveFilter = useMemo(() => {
        if (!employeeIds.length) return 'id==-1';
        return `id=in=(${employeeIds.join(',')})`;
    }, [employeeIds]);

    const [fetchData, { data, isFetching }] = useLazyGetContactsWithFiltersQuery();

    const columns = useMemo<DataTableColumnDef<ContactListItem>[]>(
        () => [
            {
                header: t('employees.person-role'),
                accessorKey: 'name',
                meta: {
                    widthWeight: 2,
                    minPx: 200,
                },
                cell: ({ row }) => (
                    <div>
                        <PartyName party={row.original} isLink plainLink variant="list" className="font-semibold" />
                        {row.original.position && (
                            <p className="text-body2 text-fg-secondary">{row.original.position}</p>
                        )}
                    </div>
                ),
            },
            {
                header: t('phone'),
                id: 'phone',
                enableSorting: false,
                meta: {
                    widthWeight: 1.2,
                    minPx: 150,
                },
                cell: ({ row }) => {
                    const { mainPhone, mainMobile } = row.original;
                    if (!mainPhone && !mainMobile) {
                        return <p className="text-body1">-</p>;
                    }
                    return (
                        <div>
                            {mainPhone && (
                                <p className="text-body1">
                                    <span className="text-fg-secondary">{t('work')}: </span>
                                    {mainPhone}
                                </p>
                            )}
                            {mainMobile && (
                                <p className="text-body1">
                                    <span className="text-fg-secondary">{t('mobile')}: </span>
                                    {mainMobile}
                                </p>
                            )}
                        </div>
                    );
                },
            },
            {
                header: t('email'),
                accessorKey: 'mainEmail',
                meta: {
                    widthWeight: 2,
                    minPx: 200,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return (
                        <div className="flex items-center">
                            <p className="text-body1">{value || '-'}</p>
                            {value && <CopyToClipboardIcon text={value} />}
                        </div>
                    );
                },
            },
            {
                id: 'actions',
                header: '',
                enableSorting: false,
                meta: {
                    // Fixed column (single edit button) — excluded from the weighted flex.
                    fixedPx: 100,
                },
                cell: ({ row }: { row: { original: ContactListItem } }) => (
                    <div className="flex justify-end">
                        <PermissionButton
                            hasAuthority={row.original.isEditable}
                            variant="outlined"
                            size="extraSmall"
                            onClick={() => navigateWithReturn(`/contact/${row.original.id}/edit`)}
                        >
                            <FormattedMessage id="edit" />
                        </PermissionButton>
                    </div>
                ),
            },
        ],
        [navigateWithReturn]
    );

    const filterConfig = useMemo<FilterConfig>(
        () => ({
            quickFilters: [],
            filters: [],
            searchBarSelectors: [{ selector: 'name' }, { selector: 'mainEmail' }],
            passiveFilter,
        }),
        [passiveFilter]
    );

    const addNewEmployee = (
        <ListCreateButton
            permissions={['CREATE.PARTIES', 'CREATE.PARTIES.CLIENTS', 'CREATE.PARTIES.LEADS_AND_NON_CLIENTS']}
            variant="contained"
            color="secondary"
            size="small"
            onClick={() =>
                navigateWithReturn('/contact/new', {
                    state: {
                        prefill: {
                            partyType: PartyType.PERSON,
                            relationshipType: RelationshipTypeCode.NON_CLIENT,
                            employedBy: [{ id: Number(contactId), name: contact?.name }],
                        },
                    },
                })
            }
            labelId="employees.add-new-employee"
            newLabelId="new-entity.employee"
        />
    );

    if (!contactId) {
        return null;
    }

    return (
        <div className="flex h-full flex-col gap-4">
            <ListTableWithFilters<ContactListItem>
                getRowHref={getContactHref}
                // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
                // stay OFF (flags default false) — this table never had them. Own column defs
                // (not shared with the global Contacts list), so opting in here is self-contained.
                columnConfigId="contacts-employees"
                columns={columns}
                data={data}
                isLoading={isFetching}
                pluralTextId="employees.employee-count"
                singularTextId="employees.employee"
                noEntriesId="employees.no-employees-found"
                noEntriesBodyId="employees.empty.at-contact-body"
                noEntriesIcon={BadgeOutlinedIcon}
                // Same action the tab header offers, prefill included.
                noEntriesAction={addNewEmployee}
                fetchData={fetchData}
                filterConfig={filterConfig}
                rowsPerPage={50}
                extraHeaderComponent={addNewEmployee}
                canRememberFilters={false}
            />
        </div>
    );
};

export default EmployeesView;
